import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";

export const SOCIALS = [
  {
    href: "https://instagram.com/incolour.media",
    label: "Instagram",
    id: "instagram",
    Icon: Instagram,
  },
  {
    href: "https://vimeo.com/incolourmedia",
    label: "Vimeo",
    id: "vimeo",
    Icon: null,
  },
  {
    href: "https://pro.imdb.com/company/co0925448/filmography/?ref_=co_knf_t",
    label: "IMDb",
    id: "imdb",
    Icon: null,
  },
];

export function SocialMark({ id, Icon }) {
  if (Icon) {
    return <Icon size={15} strokeWidth={1.8} />;
  }

  if (id === "imdb") {
    return (
      <span className="font-sans text-[9px] font-black leading-none">
        IMDb
      </span>
    );
  }

  return (
    <span className="font-serif text-[15px] font-bold italic leading-none">
      v
    </span>
  );
}

const LINKS = [
  { to: "/original", label: "Original Work", id: "original" },
  { to: "/service", label: "Service Production", id: "service" },
  { to: "/about", label: "About", id: "about" },
  { to: "/journal", label: "Journal", id: "journal" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export function Wordmark({ dark = false }) {
  return (
    <span className="flex items-center gap-2.5">
      <img
        src="/logo-mark.webp"
        alt=""
        className="h-8 w-auto"
      />

      <span className="leading-none">
        <span
          className={`font-display text-lg font-extrabold tracking-tight ${
            dark ? "text-cream" : "text-cream"
          }`}
        >
          INCOLOUR
        </span>

        <span className="block text-[8px] font-mono uppercase tracking-[0.5em] text-cream/70">
          Films
        </span>
      </span>
    </span>
  );
}

export function FullLogo({
  className = "h-10 w-auto",
}) {
  return (
    <img
      src="/logo-full.webp"
      alt="Incolour Films"
      className={className}
    />
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const trackSocialClick = (platform, location) => {
    if (!window.gtag) return;

    window.gtag("event", "social_link_click", {
      platform,
      link_location: location,
    });
  };

  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.documentElement.style.colorScheme = "dark";

    localStorage.removeItem("incolour_theme");
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (isAdmin) return null;

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 border-b border-cream/10 bg-ink/90 backdrop-blur-xl"
        data-testid="site-header"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="nav-logo"
            aria-label="Incolour Films home"
          >
            <Wordmark dark />
          </Link>

          <nav
            className="hidden items-center gap-7 lg:flex"
            aria-label="Primary"
          >
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `group relative text-sm font-medium transition-colors ${
                    isActive
                      ? "text-cream"
                      : "text-cream/60 hover:text-cream"
                  }`
                }
              >
                {l.label}

                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-orange transition-[width] duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div
              className="hidden items-center gap-1 border-r border-cream/15 pr-3 md:flex"
              data-testid="nav-socials-bar"
            >
              {SOCIALS.map(
                ({ href, label, id, Icon }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`nav-social-${id}`}
                    onClick={() => trackSocialClick(id, "desktop_nav")}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-cream/60 transition-colors hover:bg-cream/10 hover:text-orange"
                  >
                    <SocialMark id={id} Icon={Icon} />
                  </a>
                )
              )}
            </div>

            <Link
              to="/contact"
              data-testid="nav-cta-btn"
              onClick={(e) => {
                e.preventDefault();

                navigate("/contact");

                setTimeout(() => {
                  if (window.gtag) {
                    window.gtag(
                      "event",
                      "start_project_click",
                      {
                        button_location: "navbar",
                      }
                    );
                  }
                }, 250);
              }}
              className="hidden rounded-full bg-cream px-5 py-2 text-sm font-semibold text-ink transition-colors hover:bg-orange hover:text-cream lg:inline-block"
            >
              Start a project
            </Link>

            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
              className="rounded-full border border-cream/20 p-2 text-cream lg:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex flex-col bg-ink text-cream"
            initial={{
              clipPath: "inset(0 0 100% 0)",
            }}
            animate={{
              clipPath: "inset(0 0 0% 0)",
            }}
            exit={{
              clipPath: "inset(0 0 100% 0)",
            }}
            transition={{
              duration: 0.5,
              ease: [0.83, 0, 0.17, 1],
            }}
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-4 sm:px-6">
              <FullLogo className="h-7 w-auto" />

              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
                className="rounded-full border border-cream/30 p-2"
              >
                <X size={18} />
              </button>
            </div>

            <nav
              className="flex flex-1 flex-col justify-center gap-2 px-6"
              aria-label="Mobile"
            >
              {[
                {
                  to: "/",
                  label: "Home",
                  id: "home",
                },
                ...LINKS,
              ].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{
                    y: 40,
                    opacity: 0,
                  }}
                  animate={{
                    y: 0,
                    opacity: 1,
                  }}
                  transition={{
                    delay: 0.2 + i * 0.07,
                  }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="font-display text-4xl font-extrabold tracking-tight text-cream transition-colors hover:text-orange sm:text-5xl"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <div className="px-6 pb-5">
              <div
                className="flex flex-wrap items-center gap-3 border-t border-cream/10 pt-5"
                data-testid="mobile-socials"
              >
                {SOCIALS.map(({ href, label, id, Icon }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    onClick={() => trackSocialClick(id, "mobile_nav")}
                    data-testid={`mobile-social-${id}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition-colors hover:border-cream/30 hover:bg-cream/10 hover:text-orange"
                  >
                    <SocialMark id={id} Icon={Icon} />
                  </a>
                ))}
              </div>
            </div>

            <p className="px-6 pb-8 text-xs font-mono uppercase tracking-[0.25em] text-cream/50">
              Toronto — Originals &amp; Service
              Production
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
